/**
 * Audit Checklist List Page
 *
 * Shows all audit checklists for the auditor.
 * Auditors can view, create, and manage official audit checklists.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CheckSquare, Search, Filter, Plus, CheckCircle2, Clock, AlertCircle, FileText } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardBody } from '../../components/common/Card';
import { Badge } from '../../components/common/Badge';
import { Button } from '../../components/common/Button';
import { EmptyState } from '../../components/common/EmptyState';
import { ProgressBar } from '../../components/common/ProgressBar';
import { formatDate } from '../../utils/formatting';

interface AuditChecklist {
  id: string;
  applicationId: string;
  companyName: string;
  auditType: 'initial' | 'surveillance' | 'renewal';
  status: 'scheduled' | 'in_progress' | 'completed' | 'pending_review';
  progress: number;
  scheduledDate?: string;
  completedDate?: string;
  auditor: string;
  findings?: {
    conformities: number;
    minorNCs: number;
    majorNCs: number;
    observations: number;
  };
}

export const AuditChecklistListPage: React.FC = () => {
  const { t } = useTranslation(['common', 'auditor']);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [audits, setAudits] = useState<AuditChecklist[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setAudits([
        {
          id: 'app-001',
          applicationId: 'APP-2025-001',
          companyName: 'ABC Food Manufacturing Sdn Bhd',
          auditType: 'initial',
          status: 'completed',
          progress: 100,
          scheduledDate: '2025-12-15',
          completedDate: '2025-12-20',
          auditor: 'Sarah Ahmad',
          findings: {
            conformities: 42,
            minorNCs: 3,
            majorNCs: 0,
            observations: 5,
          },
        },
        {
          id: 'app-002',
          applicationId: 'APP-2025-002',
          companyName: 'Premium Bakery Industries',
          auditType: 'surveillance',
          status: 'in_progress',
          progress: 65,
          scheduledDate: '2025-12-28',
          auditor: 'Sarah Ahmad',
          findings: {
            conformities: 28,
            minorNCs: 2,
            majorNCs: 0,
            observations: 3,
          },
        },
        {
          id: 'app-003',
          applicationId: 'APP-2025-003',
          companyName: 'Global Ingredients Supply Co.',
          auditType: 'initial',
          status: 'scheduled',
          progress: 0,
          scheduledDate: '2026-01-10',
          auditor: 'Sarah Ahmad',
        },
        {
          id: 'app-004',
          applicationId: 'APP-2025-004',
          companyName: 'Tasty Snacks Manufacturing',
          auditType: 'renewal',
          status: 'pending_review',
          progress: 100,
          scheduledDate: '2025-12-22',
          completedDate: '2025-12-27',
          auditor: 'Sarah Ahmad',
          findings: {
            conformities: 45,
            minorNCs: 1,
            majorNCs: 1,
            observations: 2,
          },
        },
      ]);
      setLoading(false);
    }, 500);
  }, []);

  const getStatusBadge = (status: AuditChecklist['status']) => {
    switch (status) {
      case 'completed':
        return <Badge variant="success" dot>{t('auditor:auditChecklist.status.completed')}</Badge>;
      case 'in_progress':
        return <Badge variant="warning" dot>{t('auditor:auditChecklist.status.inProgress')}</Badge>;
      case 'scheduled':
        return <Badge variant="info" dot>{t('auditor:auditChecklist.status.scheduled')}</Badge>;
      case 'pending_review':
        return <Badge variant="warning" dot>{t('auditor:auditChecklist.status.pendingReview')}</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const getStatusIcon = (status: AuditChecklist['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-success-600" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-warning-600" />;
      case 'scheduled':
        return <FileText className="w-5 h-5 text-info-600" />;
      case 'pending_review':
        return <AlertCircle className="w-5 h-5 text-warning-600" />;
    }
  };

  const getAuditTypeBadge = (type: AuditChecklist['auditType']) => {
    const labels = {
      initial: t('auditor:auditChecklist.types.initial'),
      surveillance: t('auditor:auditChecklist.types.surveillance'),
      renewal: t('auditor:auditChecklist.types.renewal'),
    };
    return <Badge variant="primary">{labels[type]}</Badge>;
  };

  const filteredAudits = audits.filter((audit) => {
    const matchesSearch = audit.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         audit.applicationId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || audit.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <PageLayout
        pageTitle={t('auditor:auditChecklist.title')}
        breadcrumbs={[
          { label: t('common:navigation.home'), href: '/' },
          { label: t('auditor:auditChecklist.title') },
        ]}
      >
        <div className="animate-pulse space-y-4">
          <div className="h-20 bg-gray-200 rounded-lg"></div>
          <div className="h-32 bg-gray-200 rounded-lg"></div>
          <div className="h-32 bg-gray-200 rounded-lg"></div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle={t('auditor:auditChecklist.title')}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('auditor:auditChecklist.title') },
      ]}
    >
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('auditor:auditChecklist.stats.total')}</p>
                <p className="text-2xl font-bold text-gray-900">{audits.length}</p>
              </div>
              <CheckSquare className="w-8 h-8 text-primary-600" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('auditor:auditChecklist.stats.completed')}</p>
                <p className="text-2xl font-bold text-success-600">
                  {audits.filter(a => a.status === 'completed').length}
                </p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-success-600" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('auditor:auditChecklist.stats.inProgress')}</p>
                <p className="text-2xl font-bold text-warning-600">
                  {audits.filter(a => a.status === 'in_progress').length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-warning-600" />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('auditor:auditChecklist.stats.scheduled')}</p>
                <p className="text-2xl font-bold text-info-600">
                  {audits.filter(a => a.status === 'scheduled').length}
                </p>
              </div>
              <FileText className="w-8 h-8 text-info-600" />
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardBody>
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('auditor:auditChecklist.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">{t('auditor:auditChecklist.filters.all')}</option>
                <option value="scheduled">{t('auditor:auditChecklist.filters.scheduled')}</option>
                <option value="in_progress">{t('auditor:auditChecklist.filters.inProgress')}</option>
                <option value="pending_review">{t('auditor:auditChecklist.filters.pendingReview')}</option>
                <option value="completed">{t('auditor:auditChecklist.filters.completed')}</option>
              </select>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Audit Checklist List */}
      {filteredAudits.length === 0 ? (
        <EmptyState
          icon={<CheckSquare className="w-12 h-12" />}
          title={t('auditor:auditChecklist.emptyState.title')}
          description={t('auditor:auditChecklist.emptyState.description')}
          action={{
            label: t('auditor:auditChecklist.emptyState.action'),
            onClick: () => navigate('/auditor/applications'),
            icon: <Plus className="w-4 h-4" />,
          }}
        />
      ) : (
        <div className="space-y-4">
          {filteredAudits.map((audit) => (
            <Card
              key={audit.id}
              hover
              className="cursor-pointer transition-shadow"
              onClick={() => navigate(`/auditor/audits/${audit.id}`)}
            >
              <CardBody>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    {/* Status Icon */}
                    <div className="mt-1">
                      {getStatusIcon(audit.status)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">
                          {audit.companyName}
                        </h3>
                        {getStatusBadge(audit.status)}
                        {getAuditTypeBadge(audit.auditType)}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 mb-3">
                        <div>
                          <span className="font-medium">{t('auditor:auditChecklist.applicationId')}:</span>{' '}
                          {audit.applicationId}
                        </div>
                        <div>
                          <span className="font-medium">{t('auditor:auditChecklist.scheduledDate')}:</span>{' '}
                          {audit.scheduledDate ? formatDate(audit.scheduledDate) : '-'}
                        </div>
                        {audit.completedDate && (
                          <div>
                            <span className="font-medium">{t('auditor:auditChecklist.completedDate')}:</span>{' '}
                            {formatDate(audit.completedDate)}
                          </div>
                        )}
                      </div>

                      {/* Progress Bar */}
                      <div className="mb-3">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-600">{t('auditor:auditChecklist.progress')}</span>
                          <span className="font-medium text-gray-900">{audit.progress}%</span>
                        </div>
                        <ProgressBar
                          value={audit.progress}
                          max={100}
                          variant={
                            audit.progress === 100
                              ? 'success'
                              : audit.progress > 0
                              ? 'warning'
                              : 'default'
                          }
                          size="md"
                          showLabel={false}
                        />
                      </div>

                      {/* Findings Summary */}
                      {audit.findings && (
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-gray-600">
                            {t('auditor:auditChecklist.findings')}:
                          </span>
                          <span className="text-success-600 font-medium">
                            ✓ {audit.findings.conformities}
                          </span>
                          <span className="text-warning-600 font-medium">
                            ⚠ {audit.findings.minorNCs}
                          </span>
                          <span className="text-error-600 font-medium">
                            ✗ {audit.findings.majorNCs}
                          </span>
                          <span className="text-gray-500">
                            ℹ {audit.findings.observations}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/auditor/audits/${audit.id}`);
                    }}
                  >
                    {audit.status === 'completed'
                      ? t('auditor:auditChecklist.viewReport')
                      : t('auditor:auditChecklist.continue')}
                  </Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </PageLayout>
  );
};
